import React, { useState, useMemo, useRef, useEffect } from 'react';
import { todayIST } from '../utils/dates';
import { INDIA_STATES, INDIA_STATES_CITIES } from '../data/indiaCities';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { leadService, userService, brandService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../components/Spinner';
import toast from 'react-hot-toast';

// ── Self-contained state dropdown ─────────────────────────────────
function StateSelectDropdown({ value, onChange, inputCls, labelCls }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = search
    ? INDIA_STATES.filter(s => s.toLowerCase().includes(search.toLowerCase()))
    : INDIA_STATES;

  return (
    <div ref={ref} className="relative">
      <label className={labelCls}>State</label>
      <button type="button" onClick={() => { setOpen(v => !v); setSearch(''); }}
        className={`${inputCls} w-full flex items-center justify-between text-left`}>
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{value || 'Select State'}</span>
        <svg className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg mt-1" style={{ width: '100%', minWidth: 220 }}>
          {value && (
            <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
              onMouseDown={() => { onChange(''); setOpen(false); }}>
              <input type="checkbox" readOnly checked={false} className="accent-primary-600" />
              <span className="text-sm text-gray-500">Clear Selection</span>
            </div>
          )}
          <div className="px-3 py-2 border-b border-gray-100">
            <input autoFocus type="text" placeholder="Search State..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full text-sm outline-none text-gray-700 placeholder-gray-400" />
          </div>
          <ul className="max-h-52 overflow-y-auto">
            {filtered.map(s => (
              <li key={s} className="flex items-center gap-2 px-3 py-2 hover:bg-primary-50 cursor-pointer text-sm"
                onMouseDown={() => { onChange(s); setSearch(''); setOpen(false); }}>
                <input type="checkbox" readOnly checked={value === s} className="accent-primary-600" />
                {s}
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-3 text-sm text-gray-400 text-center">No results</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Self-contained city dropdown ──────────────────────────────────
function CitySelectDropdown({ value, state, onChange, inputCls, labelCls }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Reset search when state changes
  useEffect(() => { setSearch(''); setOpen(false); }, [state]);

  const cities   = INDIA_STATES_CITIES[state] || [];
  const filtered = search ? cities.filter(c => c.toLowerCase().includes(search.toLowerCase())) : cities;

  return (
    <div ref={ref} className="relative">
      <label className={labelCls}>City</label>
      <button type="button" disabled={!state}
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        className={`${inputCls} w-full flex items-center justify-between text-left ${!state ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>
          {value || (state ? 'Select City' : 'Select state first')}
        </span>
        <svg className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && state && (
        <div className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg mt-1" style={{ width: '100%', minWidth: 220 }}>
          {value && (
            <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
              onMouseDown={() => { onChange(''); setOpen(false); }}>
              <input type="checkbox" readOnly checked={false} className="accent-primary-600" />
              <span className="text-sm text-gray-500">Clear Selection</span>
            </div>
          )}
          <div className="px-3 py-2 border-b border-gray-100">
            <input autoFocus type="text" placeholder="Search City..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full text-sm outline-none text-gray-700 placeholder-gray-400" />
          </div>
          <ul className="max-h-52 overflow-y-auto">
            {filtered.map(c => (
              <li key={c} className="flex items-center gap-2 px-3 py-2 hover:bg-primary-50 cursor-pointer text-sm"
                onMouseDown={() => { onChange(c); setSearch(''); setOpen(false); }}>
                <input type="checkbox" readOnly checked={value === c} className="accent-primary-600" />
                {c}
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-3 text-sm text-gray-400 text-center">No results</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
export const AddLead = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin      = user?.role === 'admin';
  const isDealer     = user?.role === 'dealer';
  const isSalesExec  = user?.role === 'sales_executive';

  const brandsQuery = useQuery('brands', brandService.getBrands, { staleTime: 5 * 60_000 });
  const brands = brandsQuery.data?.data?.data?.brands || [];

  // Fetch all active users in downline for cascading dropdowns
  const assignableUsersQuery = useQuery(
    ['assignableUsers', user?.role],
    () => userService.getUsers({ status: 'active', limit: 200 }),
    { enabled: hasPermission('leads', 'create') && !isSalesExec }
  );

  const allUsers = (() => {
    const d = assignableUsersQuery.data?.data;
    if (!d) return [];
    return d.users || d.data?.users || [];
  })();

  // Cascading selection state — each level narrows the next
  const [selectedAdminId,  setSelectedAdminId]  = useState('');
  const [selectedDealerId, setSelectedDealerId] = useState(isDealer ? (user._id || user.id) : '');

  const parentIdOf = (u) => typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;

  // Super Admin → sees all admins
  const adminList = useMemo(() =>
    allUsers.filter(u => u.role === 'admin'),
    [allUsers]
  );

  // Dealers visible to current user — for super_admin filtered by selectedAdminId
  const dealerList = useMemo(() => {
    const dealers = allUsers.filter(u => u.role === 'dealer');
    if (isSuperAdmin) {
      if (!selectedAdminId) return [];
      return dealers.filter(d => String(parentIdOf(d)) === String(selectedAdminId));
    }
    if (isAdmin) {
      const myId = String(user._id || user.id);
      return dealers.filter(d => String(parentIdOf(d)) === myId);
    }
    return dealers;
  }, [allUsers, isSuperAdmin, isAdmin, selectedAdminId, user]);

  // Sales execs visible to current user — filtered by selectedDealerId
  const salesExecList = useMemo(() => {
    const execs = allUsers.filter(u => u.role === 'sales_executive');
    if (selectedDealerId) {
      return execs.filter(se => String(parentIdOf(se)) === String(selectedDealerId));
    }
    if (isAdmin || isDealer || isSuperAdmin) return [];
    return execs;
  }, [allUsers, selectedDealerId, isAdmin, isDealer, isSuperAdmin]);

  const today = todayIST();   // India day — toISOString gave the UTC day before 05:30 IST


  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    customerInfo: {
      name:           '',
      phone:          '',
      alternatePhone: '',
      email:          '',
      state:          '',
      city:           '',
      subcity:        '',
      pincode:        '',
      address:        '',
    },
    leadInfo: {
      enquiryDate:   today,
      brand:         '',
      model:         '',
      source:        '',
      buyingTime:    '',
      buyingTimeRaw: '',
    },
    assignment: {
      assignedTo: (isSalesExec || isSuperAdmin || isAdmin || isDealer) ? (user._id || user.id) : null,
    },
    dealerInfo: {
      _selectedDealerId: isDealer ? (user._id || user.id) : '',
      dealerName:       isDealer ? `${user.firstName} ${user.lastName}` : '',
      dealerCity:       '',
      dealerNumber:     isDealer ? (user.phone || '') : '',
      dealerEmail:      isDealer ? (user.email || '') : '',
      dealershipStatus: 'active',
    },
    status:   'new',
    priority: 'medium',
  });

  const selectedBrand   = brands.find(b => b.name === formData.leadInfo.brand);
  const availableModels = selectedBrand?.models?.filter(m => m.isActive) || [];

  // A brand/model pre-filled from an external source may not match the
  // catalog — surface it as a selectable custom entry instead of the
  // dropdown silently showing nothing.
  const brandOptions = (formData.leadInfo.brand && !brands.some(b => b.name === formData.leadInfo.brand))
    ? [...brands, { _id: '__custom_brand__', name: formData.leadInfo.brand, custom: true }]
    : brands;
  const modelOptions = (formData.leadInfo.model && !availableModels.some(m => m.name === formData.leadInfo.model))
    ? [...availableModels, { _id: '__custom_model__', name: formData.leadInfo.model, custom: true }]
    : availableModels;

  const createLeadMutation = useMutation(leadService.createLead, {
    onSuccess: () => {
      queryClient.invalidateQueries('leads');
      queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
      navigate('/leads');
    },
    onError: (error) => {
      const backendErrors = error.response?.data?.errors;
      if (backendErrors?.length) {
        // Map express-validator field errors to inline errors
        const mapped = {};
        backendErrors.forEach(e => { if (e.path) mapped[e.path] = e.msg; });
        if (Object.keys(mapped).length) {
          setErrors(mapped);
          setTimeout(() => document.querySelector('[data-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
          return;
        }
      }
      toast.error(error.response?.data?.message || 'Failed to create lead. Please try again.');
    },
  });

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    const key = `${section}.${field}`;
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  // When admin/super_admin selects a dealer, auto-fill dealer info
  const handleDealerSelect = (dealerId) => {
    setSelectedDealerId(dealerId);
    // Reset assigned sales executive when dealer changes
    handleChange('assignment', 'assignedTo', null);

    const dealer = allUsers.find(u => u._id === dealerId);
    if (dealer) {
      setFormData(prev => ({
        ...prev,
        dealerInfo: {
          ...prev.dealerInfo,
          _selectedDealerId: dealerId,
          dealerName:   `${dealer.firstName} ${dealer.lastName}`,
          dealerEmail:  dealer.email || '',
          dealerNumber: dealer.phone || '',
          dealerCity:   prev.dealerInfo.dealerCity,
          dealershipStatus: 'active',
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        dealerInfo: {
          _selectedDealerId: '',
          dealerName: '', dealerCity: '', dealerNumber: '', dealerEmail: '',
          dealershipStatus: 'active',
        },
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasPermission('leads', 'create')) {
      toast.error('You do not have permission to create leads.');
      return;
    }

    const errs = {};
    const li = formData.leadInfo;
    const ci = formData.customerInfo;
    const phoneRegex = /^[+]?[\d\s\-()]{7,15}$/;

    if (!ci.name?.trim())                       errs['customerInfo.name']         = 'Customer name is required';
    if (!ci.phone?.trim())                      errs['customerInfo.phone']        = 'Mobile number is required';
    else if (!phoneRegex.test(ci.phone.trim())) errs['customerInfo.phone']        = 'Enter a valid phone number (7–15 digits)';
    if (ci.alternatePhone?.trim() && !phoneRegex.test(ci.alternatePhone.trim()))
                                                errs['customerInfo.alternatePhone'] = 'Enter a valid alternate number';
    if (ci.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ci.email.trim()))
                                                errs['customerInfo.email']        = 'Enter a valid email address';
    if (!li.enquiryDate)                        errs['leadInfo.enquiryDate']      = 'Enquiry date is required';
    if (!li.brand)                              errs['leadInfo.brand']            = 'Brand is required';
    if (!li.model)                              errs['leadInfo.model']            = 'Model is required';
    if (!li.source)                             errs['leadInfo.source']           = 'Lead source is required';
    if (!li.buyingTime)                         errs['leadInfo.buyingTime']       = 'Buying time is required';

    if (Object.keys(errs).length) {
      setErrors(errs);
      // Scroll to first error
      setTimeout(() => document.querySelector('[data-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    setErrors({});

    // Strip internal tracking field before sending to backend
    const payload = {
      ...formData,
      dealerInfo: { ...formData.dealerInfo },
    };
    delete payload.dealerInfo._selectedDealerId;

    createLeadMutation.mutate(payload);
  };

  const inputCls = 'input text-sm';
  const labelCls = 'block text-xs text-gray-500 mb-1';
  const errCls   = 'text-xs text-red-500 mt-1';
  const inputErrCls = 'input text-sm border-red-400 focus:ring-red-400';
  const fieldCls = (key) => errors[key] ? inputErrCls : inputCls;
  const Err = ({ k }) => errors[k] ? <p className={errCls} data-error>{errors[k]}</p> : null;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/leads')} className="p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Add New Lead</h1>
            <p className="mt-0.5 text-sm text-gray-500">Create a new sales lead</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {/* ── Customer Information ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.customerInfo.name}
                  onChange={e => handleChange('customerInfo', 'name', e.target.value)}
                  className={fieldCls('customerInfo.name')} placeholder="Enter customer name"
                />
                <Err k="customerInfo.name" />
              </div>
              <div>
                <label className={labelCls}>Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  value={formData.customerInfo.phone}
                  onChange={e => handleChange('customerInfo', 'phone', e.target.value)}
                  className={fieldCls('customerInfo.phone')} placeholder="Enter mobile number"
                />
                <Err k="customerInfo.phone" />
              </div>
              <div>
                <label className={labelCls}>Alternate Number</label>
                <input
                  type="tel"
                  value={formData.customerInfo.alternatePhone}
                  onChange={e => handleChange('customerInfo', 'alternatePhone', e.target.value)}
                  className={fieldCls('customerInfo.alternatePhone')} placeholder="Enter alternate number"
                />
                <Err k="customerInfo.alternatePhone" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="email"
                  value={formData.customerInfo.email}
                  onChange={e => handleChange('customerInfo', 'email', e.target.value)}
                  className={fieldCls('customerInfo.email')} placeholder="Enter email address (optional)"
                />
                <Err k="customerInfo.email" />
              </div>
              {/* State searchable dropdown */}
              <StateSelectDropdown
                value={formData.customerInfo.state}
                onChange={s => { handleChange('customerInfo', 'state', s); handleChange('customerInfo', 'city', ''); }}
                inputCls={inputCls}
                labelCls={labelCls}
              />
              {/* City searchable dropdown */}
              <CitySelectDropdown
                value={formData.customerInfo.city}
                state={formData.customerInfo.state}
                onChange={c => handleChange('customerInfo', 'city', c)}
                inputCls={inputCls}
                labelCls={labelCls}
              />
              <div>
                <label className={labelCls}>Subcity / Area</label>
                <input
                  type="text"
                  value={formData.customerInfo.subcity}
                  onChange={e => handleChange('customerInfo', 'subcity', e.target.value)}
                  className={inputCls} placeholder="Enter subcity or area (optional)"
                />
              </div>
              <div>
                <label className={labelCls}>Pincode</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={formData.customerInfo.pincode}
                  onChange={e => handleChange('customerInfo', 'pincode', e.target.value.replace(/\D/g, ''))}
                  className={inputCls} placeholder="6-digit pincode (optional)"
                />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input
                  type="text"
                  value={formData.customerInfo.address}
                  onChange={e => handleChange('customerInfo', 'address', e.target.value)}
                  className={inputCls} placeholder="Enter address"
                />
              </div>
            </div>
          </div>

          {/* ── Lead Information ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Lead Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Enquiry Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={formData.leadInfo.enquiryDate}
                  onChange={e => handleChange('leadInfo', 'enquiryDate', e.target.value)}
                  className={fieldCls('leadInfo.enquiryDate')}
                />
                <Err k="leadInfo.enquiryDate" />
              </div>
              <div>
                <label className={labelCls}>Brand Name <span className="text-red-500">*</span></label>
                <select
                  value={formData.leadInfo.brand}
                  onChange={e => {
                    handleChange('leadInfo', 'brand', e.target.value);
                    handleChange('leadInfo', 'model', '');
                  }}
                  className={fieldCls('leadInfo.brand')}
                >
                  <option value="">
                    {brandsQuery.isLoading ? 'Loading...' : 'Select Brand'}
                  </option>
                  {brandOptions.map(b => (
                    <option key={b._id} value={b.name}>{b.name}{b.custom ? ' (custom)' : ''}</option>
                  ))}
                </select>
                <Err k="leadInfo.brand" />
              </div>
              <div>
                <label className={labelCls}>Model <span className="text-red-500">*</span></label>
                <select
                  value={formData.leadInfo.model}
                  onChange={e => handleChange('leadInfo', 'model', e.target.value)}
                  className={fieldCls('leadInfo.model')}
                  disabled={!formData.leadInfo.brand || modelOptions.length === 0}
                >
                  <option value="">
                    {!formData.leadInfo.brand
                      ? 'Select brand first'
                      : modelOptions.length === 0
                        ? 'No models available'
                        : 'Select Model'}
                  </option>
                  {modelOptions.map(m => (
                    <option key={m._id} value={m.name}>{m.name}{m.custom ? ' (custom)' : ''}</option>
                  ))}
                </select>
                <Err k="leadInfo.model" />
              </div>
              <div>
                <label className={labelCls}>Lead Source <span className="text-red-500">*</span></label>
                <select
                  value={formData.leadInfo.source}
                  onChange={e => handleChange('leadInfo', 'source', e.target.value)}
                  className={fieldCls('leadInfo.source')}
                >
                  <option value="">Select Source</option>
                  <option value="phone">Phone</option>
                  <option value="website">Website</option>
                  <option value="email">Email</option>
                  <option value="social_media">Social Media</option>
                  <option value="facebook">DOW</option>
                  <option value="referral">Referral</option>
                  <option value="walk_in">Walk In</option>
                  <option value="other">Other</option>
                </select>
                <Err k="leadInfo.source" />
              </div>
              <div>
                <label className={labelCls}>Buying Time <span className="text-red-500">*</span></label>
                <select
                  value={formData.leadInfo.buyingTime}
                  onChange={e => handleChange('leadInfo', 'buyingTime', e.target.value)}
                  className={fieldCls('leadInfo.buyingTime')}
                >
                  <option value="">Select Buying Time</option>
                  <option value="just_enquiring">Just Enquiring</option>
                  <option value="immediate">Immediate</option>
                  <option value="within_1_month">Within 1 Month</option>
                  <option value="within_3_months">Within 3 Months</option>
                  <option value="within_6_months">Within 6 Months</option>
                  <option value="other">Other (custom)</option>
                </select>
                <Err k="leadInfo.buyingTime" />
                {formData.leadInfo.buyingTime === 'other' && (
                  <input
                    type="text"
                    value={formData.leadInfo.buyingTimeRaw}
                    onChange={e => handleChange('leadInfo', 'buyingTimeRaw', e.target.value)}
                    className={`${inputCls} mt-2`}
                    placeholder="Customer's original answer"
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Assignment ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
              Assignment
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

              {/* Sales exec auto-assigns to self */}
              {isSalesExec && (
                <div>
                  <label className={labelCls}>Assigned To</label>
                  <input
                    type="text" readOnly
                    value={`${user.firstName} ${user.lastName} (You)`}
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                </div>
              )}

              {/* Super Admin: cascading Self → Admin → Dealer → Sales Exec */}
              {isSuperAdmin && (
                <>
                  <div>
                    <label className={labelCls}>Admin / Self</label>
                    <select
                      value={selectedAdminId || (formData.assignment.assignedTo === (user._id || user.id) ? '__self' : '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__self' || v === '') {
                          setSelectedAdminId('');
                          setSelectedDealerId('');
                          handleChange('assignment', 'assignedTo', user._id || user.id);
                          setFormData(prev => ({
                            ...prev,
                            dealerInfo: {
                              _selectedDealerId: '',
                              dealerName: '', dealerCity: '', dealerNumber: '', dealerEmail: '',
                              dealershipStatus: 'active',
                            },
                          }));
                        } else {
                          setSelectedAdminId(v);
                          setSelectedDealerId('');
                          handleChange('assignment', 'assignedTo', v);
                          setFormData(prev => ({
                            ...prev,
                            dealerInfo: {
                              _selectedDealerId: '',
                              dealerName: '', dealerCity: '', dealerNumber: '', dealerEmail: '',
                              dealershipStatus: 'active',
                            },
                          }));
                        }
                      }}
                      className={inputCls}
                    >
                      <option value="__self">{user.firstName} {user.lastName} (You)</option>
                      {adminList.map(a => (
                        <option key={a._id} value={a._id}>{a.firstName} {a.lastName} (Admin)</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2 — pick a Dealer under selected Admin (optional) */}
                  {selectedAdminId && (
                    <div>
                      <label className={labelCls}>Dealer (optional)</label>
                      <select
                        value={selectedDealerId}
                        onChange={e => {
                          const did = e.target.value;
                          setSelectedDealerId(did);
                          if (did) {
                            const dealer = allUsers.find(u => u._id === did);
                            handleChange('assignment', 'assignedTo', did);
                            if (dealer) {
                              setFormData(prev => ({
                                ...prev,
                                dealerInfo: {
                                  ...prev.dealerInfo,
                                  _selectedDealerId: did,
                                  dealerName:   `${dealer.firstName} ${dealer.lastName}`,
                                  dealerEmail:  dealer.email || '',
                                  dealerNumber: dealer.phone || '',
                                  dealershipStatus: 'active',
                                },
                              }));
                            }
                          } else {
                            // Clearing dealer → keep with admin
                            handleChange('assignment', 'assignedTo', selectedAdminId);
                            setFormData(prev => ({
                              ...prev,
                              dealerInfo: {
                                _selectedDealerId: '',
                                dealerName: '', dealerCity: '', dealerNumber: '', dealerEmail: '',
                                dealershipStatus: 'active',
                              },
                            }));
                          }
                        }}
                        className={inputCls}
                      >
                        <option value="">— Keep with Admin —</option>
                        {dealerList.map(d => (
                          <option key={d._id} value={d._id}>{d.firstName} {d.lastName} (Dealer)</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Step 3 — pick a Sales Executive under selected Dealer (optional) */}
                  {selectedDealerId && salesExecList.length > 0 && (
                    <div>
                      <label className={labelCls}>Sales Executive (optional)</label>
                      <select
                        value={formData.assignment.assignedTo === selectedDealerId ? '' : (formData.assignment.assignedTo || '')}
                        onChange={e => {
                          const sid = e.target.value;
                          handleChange('assignment', 'assignedTo', sid || selectedDealerId);
                        }}
                        className={inputCls}
                      >
                        <option value="">— Keep with Dealer —</option>
                        {salesExecList.map(se => (
                          <option key={se._id} value={se._id}>
                            {se.firstName} {se.lastName} (Sales Executive)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* Admin: cascading Self → Dealer → Sales Exec */}
              {isAdmin && (
                <>
                  <div>
                    <label className={labelCls}>Dealer / Self <span className="text-red-500">*</span></label>
                    <select
                      value={selectedDealerId || (formData.assignment.assignedTo === (user._id || user.id) ? '__self' : '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__self' || v === '') {
                          setSelectedDealerId('');
                          handleChange('assignment', 'assignedTo', user._id || user.id);
                          setFormData(prev => ({
                            ...prev,
                            dealerInfo: {
                              _selectedDealerId: '',
                              dealerName: '', dealerCity: '', dealerNumber: '', dealerEmail: '',
                              dealershipStatus: 'active',
                            },
                          }));
                        } else {
                          const dealer = allUsers.find(u => u._id === v);
                          setSelectedDealerId(v);
                          handleChange('assignment', 'assignedTo', v);
                          if (dealer) {
                            setFormData(prev => ({
                              ...prev,
                              dealerInfo: {
                                ...prev.dealerInfo,
                                _selectedDealerId: v,
                                dealerName:   `${dealer.firstName} ${dealer.lastName}`,
                                dealerEmail:  dealer.email || '',
                                dealerNumber: dealer.phone || '',
                                dealershipStatus: 'active',
                              },
                            }));
                          }
                        }
                      }}
                      className={inputCls}
                      required
                    >
                      <option value="__self">{user.firstName} {user.lastName} (You)</option>
                      {dealerList.map(d => (
                        <option key={d._id} value={d._id}>{d.firstName} {d.lastName} (Dealer)</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2 — pick a Sales Executive under selected Dealer (optional) */}
                  {selectedDealerId && salesExecList.length > 0 && (
                    <div>
                      <label className={labelCls}>Sales Executive (optional)</label>
                      <select
                        value={formData.assignment.assignedTo === selectedDealerId ? '' : (formData.assignment.assignedTo || '')}
                        onChange={e => {
                          const sid = e.target.value;
                          handleChange('assignment', 'assignedTo', sid || selectedDealerId);
                        }}
                        className={inputCls}
                      >
                        <option value="">— Keep with Dealer —</option>
                        {salesExecList.map(se => (
                          <option key={se._id} value={se._id}>
                            {se.firstName} {se.lastName} (Sales Executive)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* Dealer: auto-set themselves, assign to Sales Executive */}
              {isDealer && (
                <>
                  <div>
                    <label className={labelCls}>Dealer</label>
                    <input
                      type="text" readOnly
                      value={`${user.firstName} ${user.lastName} (You)`}
                      className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Assign To</label>
                    <select
                      value={formData.assignment.assignedTo || (user._id || user.id)}
                      onChange={e => handleChange('assignment', 'assignedTo', e.target.value || (user._id || user.id))}
                      className={inputCls}
                    >
                      <option value={user._id || user.id}>{user.firstName} {user.lastName} (You)</option>
                      {salesExecList.map(u => (
                        <option key={u._id} value={u._id}>
                          {u.firstName} {u.lastName} (Sales Executive)
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

            </div>
          </div>

          {/* ── Dealer Details (auto-filled, shown when dealer selected) ── */}
          {(selectedDealerId || isDealer) && !isSuperAdmin && !isAdmin && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                Dealer Details
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Dealer City</label>
                  <input
                    type="text"
                    value={formData.dealerInfo.dealerCity}
                    onChange={e => handleChange('dealerInfo', 'dealerCity', e.target.value)}
                    className={inputCls} placeholder="Enter dealer city"
                  />
                </div>
                <div>
                  <label className={labelCls}>Dealer Mobile</label>
                  <input
                    type="tel"
                    value={formData.dealerInfo.dealerNumber}
                    readOnly
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                </div>
                <div>
                  <label className={labelCls}>Dealer Email</label>
                  <input
                    type="email"
                    value={formData.dealerInfo.dealerEmail}
                    readOnly
                    className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  />
                </div>
                <div>
                  <label className={labelCls}>Dealership Status</label>
                  <select
                    value={formData.dealerInfo.dealershipStatus}
                    onChange={e => handleChange('dealerInfo', 'dealershipStatus', e.target.value)}
                    className={inputCls}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Validation summary */}
          {Object.keys(errors).length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2">
              <svg className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-700">Please fix the following errors:</p>
                <ul className="mt-1 text-xs text-red-600 list-disc list-inside space-y-0.5">
                  {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/leads')}
              className="btn-outline-secondary"
              disabled={createLeadMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createLeadMutation.isLoading}
            >
              {createLeadMutation.isLoading ? (
                <><Spinner size="sm" className="mr-2" />Creating...</>
              ) : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
